import { useCallback, useEffect, useRef, useState } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme";

const PALETTE = {
  dark: { bg: "#0D121F", text: "#94A3B8", grid: "#1E293B55", border: "#1E293B" },
  light: { bg: "#FFFFFF", text: "#475569", grid: "#E2E8F0", border: "#CBD5E1" },
};
const themed = (t) => {
  const c = PALETTE[t] || PALETTE.dark;
  return { layout: { background: { color: c.bg }, textColor: c.text }, grid: { vertLines: { color: c.grid }, horzLines: { color: c.grid } },
    rightPriceScale: { borderColor: c.border }, timeScale: { borderColor: c.border } };
};

const IST_OFFSET = 19800;
const VIEW_KEY = "orb-chart-view";
const PREF_KEY = "orb-chart-prefs";
const base = {
  layout: { background: { color: "#0D121F" }, textColor: "#94A3B8", fontFamily: "IBM Plex Mono" },
  grid: { vertLines: { color: "#1E293B55" }, horzLines: { color: "#1E293B55" } },
  rightPriceScale: { borderColor: "#1E293B" },
  timeScale: { borderColor: "#1E293B", timeVisible: true, secondsVisible: false, shiftVisibleRangeOnNewBar: false },
  crosshair: { mode: CrosshairMode.Normal },
  localization: { locale: "en-IN" },
};
const BOOKS = { paper: { label: "Paper", color: "#94A3B8", tag: "P" }, dry: { label: "Dry run", color: "#818CF8", tag: "D" }, live: { label: "Live", color: "#F59E0B", tag: "L" } };
const LEVELS = [["pdh", "PDH", "#34D399"], ["pdl", "PDL", "#F87171"], ["pdc", "PDC", "#CBD5E1"], ["pdv", "PD VWAP", "#A78BFA"], ["today_open", "Open", "#FBBF24"]];
const WEEKLY = [["pwh", "PWH", "#22D3EE"], ["pwl", "PWL", "#FB7185"], ["week_open", "Wk open", "#FDE68A"]];
const hm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false }) : "—");
const REASON = { target: "Target hit", stop: "Stop hit", time: "Time exit", manual: "Manual exit", daily_limit: "Daily loss limit", strategy_budget: "Strategy budget", expiry: "Expiry", kill: "Kill switch", emergency: "Emergency exit" };

const MarkerTip = ({ tip }) => (
  <div data-testid="marker-tooltip" className="pointer-events-none absolute z-20 w-64 rounded-md border border-slate-700 bg-[#0D121F]/95 p-2.5 text-[11px] shadow-xl backdrop-blur"
    style={{ left: Math.max(4, tip.x - 270 > 0 && tip.flip ? tip.x - 270 : tip.x + 14), top: Math.max(4, tip.y - 20) }}>
    {tip.items.map((m) => (
      <div key={m.id} className="border-b border-slate-800 py-1 last:border-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold" style={{ color: BOOKS[m.book].color }}>{BOOKS[m.book].label} · {m.strategy}</span>
          <span className={(m.pnl || 0) >= 0 ? "up" : "down"}>{m.pnl != null ? inr(m.pnl) : ""}</span>
        </div>
        <div className="num text-slate-300">{m.strikes || "—"}{m.expiry ? ` · exp ${m.expiry}` : ""}</div>
        <div className="num text-slate-400">Entry net {m.debit != null ? `${m.debit >= 0 ? "debit" : "credit"} ₹${Math.abs(m.debit).toFixed(2)}` : "—"}{m.qty ? ` × ${m.qty}` : ""}</div>
        <div className="num text-slate-400">In {hm(m.opened_at)} → Out {m.status === "closed" ? hm(m.closed_at) : "open"}{m.exit_reason ? ` · ${REASON[m.exit_reason] || m.exit_reason}` : ""}</div>
      </div>
    ))}
  </div>
);
const UP = ["LONG", "COVERED", "VOLATILE"];

const SESSION_MIN = 375;
const stepOf = (rows) => (rows.length > 1 ? Math.max(60, rows[rows.length - 1].ts - rows[rows.length - 2].ts) : 300);
const sessionBars = (rows) => Math.ceil((SESSION_MIN * 60) / stepOf(rows));
const line = (rows, key) => rows.filter((r) => r[key] !== null).map((r) => ({ time: r.ts + IST_OFFSET, value: r[key] }));
const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const inr = (v) => `${v >= 0 ? "+" : "−"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;

const barTime = (rows, t) => {
  if (!rows.length || t == null) return null;
  let hit = rows[0].ts;
  for (const r of rows) { if (r.ts <= t) hit = r.ts; else break; }
  return t < rows[0].ts ? null : hit + IST_OFFSET;
};

const buildMarkers = (rows, markers, show) => {
  const out = [];
  for (const m of markers) {
    if (!show[m.book]) continue;
    const b = BOOKS[m.book];
    const up = UP.includes(m.direction);
    const et = barTime(rows, m.entry_ts);
    if (et) out.push({ time: et, position: up ? "belowBar" : "aboveBar", color: b.color, shape: up ? "arrowUp" : "arrowDown", text: b.tag, id: `${m.id}-in` });
    const xt = barTime(rows, m.exit_ts);
    if (xt) out.push({ time: xt, position: up ? "aboveBar" : "belowBar", color: (m.pnl || 0) >= 0 ? "#10B981" : "#EF4444", shape: "circle", text: `${b.tag} ${inr(m.pnl || 0)}`, id: `${m.id}-out` });
  }
  return out.sort((a, b) => a.time - b.time);
};

const Chip = ({ on, color, children, onClick, id }) => (
  <button data-testid={id} onClick={onClick} aria-pressed={on}
    className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${on ? "border-slate-600 text-slate-200" : "border-slate-800 text-slate-600"}`}>
    <span className="h-2 w-2 rounded-full" style={{ background: on ? color : "#334155" }} />{children}
  </button>
);

const EMPTY = { candles: [], markers: [], levels: null };
const RANGES = [["today", "Today"], ["1w", "1W"], ["3w", "3W"]];
const IVS = [[5, "5m"], [15, "15m"], [60, "1h"]];

const Seg = ({ items, value, onPick, id }) => (
  <div className="flex overflow-hidden rounded-md border border-slate-700" data-testid={id}>
    {items.map(([v, l]) => (
      <button key={v} data-testid={`${id}-${v}`} aria-pressed={value === v} onClick={() => onPick(v)}
        className={`px-2 py-0.5 text-[11px] transition-colors ${value === v ? "bg-indigo-500/20 text-indigo-200" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
    ))}
  </div>
);

export const CandleChart = ({ candles: liveCandles, rsiLong, rsiShort, showBands = false, levels: liveLevels = null, markers: liveMarkers = [] }) => {
  const mainRef = useRef(null);
  const rsiRef = useRef(null);
  const charts = useRef(null);
  const [prefs, setPrefs] = useState(() => ({ paper: true, dry: true, live: true, levels: true, weekly: false, ...load(PREF_KEY, {}) }));
  const [tip, setTip] = useState(null);
  const theme = useTheme();
  useEffect(() => {
    const c = charts.current;
    if (!c) return;
    c.main.applyOptions(themed(theme));
    c.rsi.applyOptions(themed(theme));
  }, [theme]);
  const tipData = useRef({ rows: [], markers: [], prefs: {} });
  const [custom, setCustom] = useState(false);
  const setPref = (k, v) => setPrefs((p) => { const n = { ...p, [k]: v }; localStorage.setItem(PREF_KEY, JSON.stringify(n)); return n; });
  const toggle = (k) => setPref(k, !prefs[k]);
  const mode = prefs.range || "today";
  const hiv = prefs.hiv || 15;
  const [hist, setHist] = useState(null);
  useEffect(() => {
    if (mode === "today") { setHist(null); return undefined; }
    let alive = true;
    const get = () => api.get("/chart/history", { params: { days: mode === "1w" ? 7 : 21, interval: hiv } }).then((r) => alive && setHist(r.data)).catch(() => {});
    setHist(null);
    get();
    const id = setInterval(get, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [mode, hiv]);
  const src = mode === "today" ? { candles: liveCandles, markers: liveMarkers, levels: liveLevels } : hist || EMPTY;
  const { candles, markers, levels } = src;
  const viewKey = `${VIEW_KEY}:${mode}${mode === "today" ? "" : `:${hiv}`}`;
  const defaultRange = useCallback((rows) => (mode === "today"
    ? { from: -3, to: Math.max(rows.length, sessionBars(rows)) + 1 } : { from: -3, to: rows.length + 4 }), [mode]);

  const setRange = useCallback((r) => {
    const c = charts.current;
    c.prog = true;
    c.main.timeScale().setVisibleLogicalRange(r);
    setTimeout(() => { c.prog = false; }, 0);
  }, []);

  useEffect(() => {
    const main = createChart(mainRef.current, { ...base, autoSize: true });
    const rsi = createChart(rsiRef.current, { ...base, autoSize: true, timeScale: { ...base.timeScale, visible: false } });
    const s = {
      candle: main.addCandlestickSeries({ upColor: "#10B981", downColor: "#EF4444", borderVisible: false, wickUpColor: "#10B981", wickDownColor: "#EF4444" }),
      vwap: main.addLineSeries({ color: "#8B5CF6", lineWidth: 2, priceLineVisible: false, lastValueVisible: false }),
      ema_f: main.addLineSeries({ color: "#3B82F6", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }),
      ema_s: main.addLineSeries({ color: "#F59E0B", lineWidth: 1, priceLineVisible: false, lastValueVisible: false }),
      bb_u: main.addLineSeries({ color: "#14B8A6", lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false }),
      bb_l: main.addLineSeries({ color: "#14B8A6", lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false }),
      rsi: rsi.addLineSeries({ color: "#EC4899", lineWidth: 1, priceLineVisible: false }),
      lines: [],
    };
    main.applyOptions(themed(document.documentElement.classList.contains("light") ? "light" : "dark"));
    rsi.applyOptions(themed(document.documentElement.classList.contains("light") ? "light" : "dark"));
    const c = { main, rsi, s, count: 0, day: null, prog: false, user: false, input: 0, levelPx: [], viewKey: VIEW_KEY };
    s.candle.applyOptions({
      autoscaleInfoProvider: (orig) => {
        const r = orig();
        if (!r || !c.levelPx.length) return r;
        return { ...r, priceRange: { minValue: Math.min(r.priceRange.minValue, ...c.levelPx), maxValue: Math.max(r.priceRange.maxValue, ...c.levelPx) } };
      },
    });
    charts.current = c;
    const mark = () => { c.input = Date.now(); };
    const el = mainRef.current;
    ["wheel", "pointerdown", "pointermove", "touchstart", "keydown"].forEach((e) => el.addEventListener(e, mark, { passive: true }));
    main.timeScale().subscribeVisibleLogicalRangeChange((r) => {
      if (!r) return;
      rsi.timeScale().setVisibleLogicalRange(r);
      if (c.prog || !c.count || Date.now() - c.input > 1500) return;
      c.user = true;
      setCustom(true);
      localStorage.setItem(c.viewKey, JSON.stringify({ day: c.day, from: r.from, to: r.to, follow: r.to >= c.count - 1 }));
    });
    main.subscribeCrosshairMove((param) => {
      const { rows, markers: ms, prefs: pf } = tipData.current;
      if (!param.time || !param.point) { setTip(null); return; }
      const items = ms.filter((m) => pf[m.book] && (barTime(rows, m.entry_ts) === param.time || barTime(rows, m.exit_ts) === param.time));
      setTip(items.length ? { x: param.point.x, y: param.point.y, flip: param.point.x > el.clientWidth / 2, items } : null);
    });
    return () => { ["wheel", "pointerdown", "pointermove", "touchstart", "keydown"].forEach((e) => el.removeEventListener(e, mark)); main.remove(); rsi.remove(); };
  }, []);

  useEffect(() => {
    const c = charts.current;
    if (!c) return;
    const { s } = c;
    c.prog = true;
    s.candle.setData(candles.map((r) => ({ time: r.ts + IST_OFFSET, open: r.open, high: r.high, low: r.low, close: r.close })));
    ["vwap", "ema_f", "ema_s", "rsi"].forEach((k) => s[k].setData(line(candles, k)));
    ["bb_u", "bb_l"].forEach((k) => s[k].setData(showBands ? line(candles, k) : []));
    setTimeout(() => { c.prog = false; }, 0);
    const n = candles.length;
    c.viewKey = viewKey;
    if (!n) { c.day = null; c.count = 0; return; }
    const day = `${viewKey}|${new Date((candles[0].ts + IST_OFFSET) * 1000).toISOString().slice(0, 10)}`;
    const saved = load(viewKey, null);
    if (day !== c.day) {
      c.day = day;
      if (saved && saved.day === day) {
        c.user = true; setCustom(true);
        const shift = saved.follow ? Math.max(0, n - 1 - Math.floor(saved.to)) : 0;
        setRange({ from: saved.from + shift, to: saved.to + shift });
      } else {
        c.user = false; setCustom(false);
        localStorage.removeItem(viewKey);
        setRange(defaultRange(candles));
      }
    } else if (n !== c.count) {
      const delta = n - c.count;
      if (!c.user) setRange(defaultRange(candles));
      else if (saved?.follow && n - 1 > saved.to - 1) {
        const nr = { from: saved.from + delta, to: saved.to + delta };
        setRange(nr);
        localStorage.setItem(viewKey, JSON.stringify({ ...saved, ...nr }));
      }
    }
    c.count = n;
  }, [candles, showBands, setRange, viewKey, defaultRange]);

  useEffect(() => {
    const c = charts.current;
    if (!c) return;
    const { s } = c;
    s.lines.forEach(([ser, pl]) => ser.removePriceLine(pl));
    s.lines = [];
    const last = candles[candles.length - 1];
    const pl = (price, color, title, style = 2) => s.lines.push([s.candle, s.candle.createPriceLine({ price, color, lineStyle: style, lineWidth: 1, title, axisLabelVisible: true })]);
    if (mode === "today" && last?.orh) { pl(last.orh, "#6366F1", "OR High"); pl(last.orl, "#6366F1", "OR Low"); }
    const groups = [...(prefs.levels ? LEVELS : []), ...(prefs.weekly ? WEEKLY : [])];
    c.levelPx = levels ? groups.map(([k]) => levels[k]).filter((v) => v != null) : [];
    if (levels) groups.forEach(([k, t, col]) => levels[k] != null && pl(levels[k], `${col}99`, t, ["today_open", "week_open"].includes(k) ? 1 : 3));
    tipData.current = { rows: candles, markers: markers || [], prefs };
    s.lines.push([s.rsi, s.rsi.createPriceLine({ price: rsiLong, color: "#10B98188", lineStyle: 2, lineWidth: 1, title: String(rsiLong) })]);
    s.lines.push([s.rsi, s.rsi.createPriceLine({ price: rsiShort, color: "#EF444488", lineStyle: 2, lineWidth: 1, title: String(rsiShort) })]);
    s.candle.setMarkers(buildMarkers(candles, markers || [], prefs));
  }, [candles, levels, markers, prefs, rsiLong, rsiShort, mode]);

  const reset = () => {
    const c = charts.current;
    localStorage.removeItem(viewKey);
    c.user = false; setCustom(false);
    setRange(defaultRange(candles));
  };
  const zoom = (f) => {
    const c = charts.current;
    const r = c.main.timeScale().getVisibleLogicalRange();
    if (!r) return;
    const w = Math.min(Math.max(8, (r.to - r.from) * f), Math.max(candles.length + 10, 20));
    const right = Math.min(r.to, candles.length + 2);
    c.input = Date.now();
    c.main.timeScale().setVisibleLogicalRange({ from: right - w, to: right });
  };
  const pickIv = (v) => { charts.current.input = 0; setPref("hiv", v); };
  const pickMode = (v) => { charts.current.input = 0; setCustom(!!load(`${VIEW_KEY}:${v}${v === "today" ? "" : `:${hiv}`}`, null)); setPref("range", v); };
  const counts = Object.fromEntries(Object.keys(BOOKS).map((k) => [k, (markers || []).filter((m) => m.book === k).length]));

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="chart-range-controls">
        <Seg id="seg-chart-range" items={RANGES} value={mode} onPick={pickMode} />
        {mode !== "today" && <Seg id="seg-chart-interval" items={IVS} value={hiv} onPick={pickIv} />}
        {mode !== "today" && hist && <span className="num text-[10px] text-slate-500" data-testid="chart-history-info">{hist.candles.length ? `${hist.from} → ${hist.to} · ${hist.candles.length} candles` : "No candles in this range yet"}</span>}
        {mode !== "today" && !hist && <span className="text-[10px] text-slate-500" data-testid="chart-history-loading">Loading history…</span>}
        <div className="ml-auto flex items-center gap-1">
          <button data-testid="button-chart-zoom-out" title="Zoom out" onClick={() => zoom(1.5)} className="flex h-6 w-6 items-center justify-center rounded border border-slate-700 text-slate-300 transition-colors hover:border-indigo-500 hover:text-indigo-200"><Minus size={12} /></button>
          <button data-testid="button-chart-zoom-in" title="Zoom in" onClick={() => zoom(1 / 1.5)} className="flex h-6 w-6 items-center justify-center rounded border border-slate-700 text-slate-300 transition-colors hover:border-indigo-500 hover:text-indigo-200"><Plus size={12} /></button>
        </div>
      </div>
      <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="chart-controls">
        {Object.entries(BOOKS).map(([k, b]) => (
          <Chip key={k} id={`chip-markers-${k}`} on={prefs[k]} color={b.color} onClick={() => toggle(k)}>{b.label} <span className="text-slate-500">{counts[k]}</span></Chip>
        ))}
        <Chip id="chip-levels" on={prefs.levels} color="#A78BFA" onClick={() => toggle("levels")}>Prev-day levels</Chip>
        <Chip id="chip-weekly-levels" on={prefs.weekly} color="#22D3EE" onClick={() => toggle("weekly")}>Weekly levels</Chip>
        {prefs.weekly && levels?.pwh != null && <span className="num text-[10px] text-slate-500" data-testid="chart-weekly-info">PWH {levels.pwh.toFixed(1)} · PWL {levels.pwl.toFixed(1)} · Wk open {levels.week_open?.toFixed(1)}</span>}
        {prefs.levels && levels?.prev_date && <span className="num text-[10px] text-slate-500" data-testid="chart-levels-info">PDH {levels.pdh?.toFixed(1)} · PDL {levels.pdl?.toFixed(1)} · PDC {levels.pdc?.toFixed(1)} ({levels.prev_date})</span>}
        <button data-testid="button-chart-reset" onClick={reset} disabled={!custom}
          className="ml-auto flex items-center gap-1 rounded px-2 py-0.5 text-[11px] text-indigo-300 transition-colors hover:text-indigo-200 disabled:text-slate-600">
          <RotateCcw size={11} /> Reset view
        </button>
      </div>
      <div className="relative">
        <div ref={mainRef} data-testid="chart-candlestick-nifty" className="h-[340px] w-full sm:h-[400px]" />
        {tip && <MarkerTip tip={tip} />}
      </div>
      <div className="eyebrow mt-2 px-1">RSI</div>
      <div ref={rsiRef} data-testid="chart-rsi-nifty" className="h-[110px] w-full" />
    </div>
  );
};
